import { useContext, useEffect, useState, useCallback, useMemo } from 'react';
import {
    Box,
    CircularProgress,
    Grid,
    InputAdornment,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    TextField,
    Typography
} from '@mui/material';
import { Search } from '@mui/icons-material';
// Our Components
import { AuthContext } from 'context/auth-context';
import { CardContext } from 'context/cards/cardContext';
import { OPEN_EDIT_CARD_FORM_MODAL, OPEN_EDIT_CARD_FUNDS_MODAL, SET_CARD, SET_CARDS } from 'context/cards/types';
import CardTableRow from './CardTableRow/CardTableRow';
import FilterByCardholder from './FilterByCardholders';
import TableTitle from 'components/shared/Tables/TableTitle';
import useDebounce from 'Hooks/useDebounce';
// Services
import { Cards, Users } from 'lib/api';
// Utils and Helpers
import { hasCreditModel, hasPrepaidModel } from 'helpers/businesses';
import { USER_ROLES } from 'helpers/constants';
// Assets
import { ReactComponent as NoCardsYetIcon } from 'assets/cardsSection/NoCardsYet.svg';
import './CardsTable.css';
import { useLocation } from 'react-router-dom/cjs/react-router-dom.min';

let TableColumns = [
    { label: 'USER' },
    { label: 'CATEGORY' },
    { label: 'DEPT' },
    { label: 'TYPE' },
    { label: 'STATUS' },
    { label: 'LIMIT' },
    { label: 'AVAILABLE BALANCE' },
    { label: '' }
];

const CardsTable = () => {
    const { cards, total, dispatch, shouldUpdateCardList } = useContext(CardContext);
    const { businessInfo, userInfo } = useContext(AuthContext);
    const [cardLast4, setCardLast4] = useState('');
    const debouncedCardLast4 = useDebounce(cardLast4, 500);
    const [cardholders, setCardholders] = useState([]);
    const [selectedCardholders, setSelectedCardholders] = useState([]);
    const [requestedCardByEmail, setRequestedCardByEmail] = useState({});
    const [pagination, setPagination] = useState({
        page: 0,
        rowsPerPage: 10
    });
    const [isTableLoading, setIsTableLoading] = useState(false);
    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const cardIdToEditLimit = queryParams.get('cardId');

    const fetchCards = useCallback(async () => {
        setIsTableLoading(true);
        const selectedIds = selectedCardholders.map(cardholder => cardholder.id);
        try {
            const queryParams = {
                ...(debouncedCardLast4 && { last4: debouncedCardLast4 }),
                cardholders: selectedIds.join(),
                page: pagination.page,
                limit: pagination.rowsPerPage
            };
            const cardsMetadata = await Cards.getCards(queryParams);
            dispatch({
                type: SET_CARDS,
                payload: cardsMetadata
            });
        } catch (error) {
            console.error(error);
        }
        setIsTableLoading(false);
    }, [dispatch, pagination.page, pagination.rowsPerPage, selectedCardholders, debouncedCardLast4]);
    useEffect(() => {
        fetchCards();
    }, [fetchCards, shouldUpdateCardList, pagination.page, pagination.rowsPerPage, debouncedCardLast4]);

    const fetchUsers = useCallback(async () => {
        try {
            const queryParams = {
                limit: 100,
                order: 'DESC'
            };
            const { results } = await Users.getUsers(queryParams);
            setCardholders(results);
        } catch (error) {
            console.error(error);
        }
    }, []);
    useEffect(() => {
        fetchUsers();
    }, [fetchUsers]);

    useEffect(() => {
        const reviewCardEditLimitRequest = async () => {
            if (!cardIdToEditLimit) return;
            const cardToEditLimit = await Cards.getCard(cardIdToEditLimit);
            if (cardToEditLimit) {
                dispatch({
                    type: SET_CARD,
                    payload: cardToEditLimit
                });
                if (hasCreditModel(businessInfo)) {
                    dispatch({ type: OPEN_EDIT_CARD_FORM_MODAL });
                }
                if (hasPrepaidModel(businessInfo)) {
                    dispatch({ type: OPEN_EDIT_CARD_FUNDS_MODAL });
                }
            }
        };
        reviewCardEditLimitRequest();
    }, [businessInfo, cardIdToEditLimit, dispatch]);

    TableColumns = useMemo(() => {
        if (hasPrepaidModel(businessInfo)) {
            return TableColumns.filter(column => !['TYPE', 'LIMIT'].includes(column.label));
        }
        return TableColumns;
    }, [businessInfo]);

    const getTableHeaderCells = () =>
        TableColumns.reduce((tableCells, currentCell, index) => {
            return tableCells.concat(
                <TableCell
                    key={index}
                    className="table-header-cell"
                    sx={{
                        textAlign: ['LIMIT', 'AVAILABLE BALANCE'].includes(currentCell.label)
                            ? 'right'
                            : cards?.length === 0 || !cards
                            ? 'center'
                            : 'left'
                    }}>
                    {currentCell.label}
                </TableCell>
            );
        }, []);

    const handlePageChange = (event, newPage) => {
        setPagination({ ...pagination, page: newPage });
    };

    const handleRowsPerPageChange = event => {
        setPagination({
            ...pagination,
            rowsPerPage: Number(event?.target?.value)
        });
    };

    const handleLast4FilterChange = ({ target: input }) => {
        setCardLast4(input.value);
        setPagination({
            page: 0,
            rowsPerPage: 10
        });
    };

    useEffect(() => {
        const verifyRedirectFromReviewCardEmail = async () => {
            let params = new URLSearchParams(window.location.search);
            let cardRequestId = params.get('cardRequestId');
            if (!cardRequestId) return;
            const cardRequested = await Cards.getCard(cardRequestId);
            if (cardRequested) {
                setRequestedCardByEmail(cardRequested);
            }
        };
        verifyRedirectFromReviewCardEmail();
    }, []);

    return (
        <Box className="cards-table-container">
            <TableTitle>All Cards ({total})</TableTitle>

            {/* CARDS FILTERS */}
            <Grid container columnSpacing={{ md: 2 }} marginBottom="25px">
                {userInfo.id_role === USER_ROLES.ADMIN && (
                    <Grid item md={3}>
                        <TextField
                            name="last4"
                            variant="outlined"
                            placeholder="Search a card"
                            size="small"
                            fullWidth
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <Search />
                                    </InputAdornment>
                                )
                            }}
                            onChange={handleLast4FilterChange}
                            value={cardLast4}
                        />
                    </Grid>
                )}
                <Grid item md={3}>
                    <FilterByCardholder
                        cardholders={cardholders}
                        setCardholders={setCardholders}
                        selectedCardholders={selectedCardholders}
                        setSelectedCardholders={setSelectedCardholders}
                        setPagination={setPagination}
                    />
                </Grid>
            </Grid>

            <TableContainer id="table-container" component={Paper} sx={{ maxHeight: '700px' }}>
                <Table stickyHeader size="small" aria-label="sticky table">
                    <TableHead sx={{ position: 'relative', zIndex: 1 }}>
                        <TableRow sx={{ bgcolor: '#F1F3F7' }}>
                            <TableCell className="table-header-cell" />
                            {getTableHeaderCells()}
                        </TableRow>
                    </TableHead>
                    <TableBody sx={{ height: cards.length > 0 ? 'auto' : '600px', maxWidth: '80%' }}>
                        {cards.map(card => (
                            <CardTableRow key={card.id} row={card} requestedCardByEmail={requestedCardByEmail} />
                        ))}
                        {isTableLoading && (
                            <TableRow>
                                <TableCell colSpan={9} sx={{ textAlign: 'center' }}>
                                    <CircularProgress />
                                </TableCell>
                            </TableRow>
                        )}
                        {!isTableLoading && cards.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={9} sx={{ textAlign: 'center' }}>
                                    <NoCardsYetIcon />
                                    <Typography paragraph className="no-cards-text">
                                        You don't have any Cards yet!
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
            <TablePagination
                rowsPerPageOptions={[5, 10, 25, 100]}
                component="div"
                count={total}
                rowsPerPage={pagination.rowsPerPage}
                page={pagination.page}
                onPageChange={handlePageChange}
                onRowsPerPageChange={handleRowsPerPageChange}
            />
        </Box>
    );
};

export default CardsTable;
